import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { log } from '../lib/Logger';
import { RestAdminApi } from './RestAdminApi';
import { AppThunk } from './store';

// XXX global scope.
const api = new RestAdminApi('https://www.whoisnemos.com/api');

export interface SessionSlice {
  userRole: UserRole;
  clearances: Clearance[];
  loginStatus: LoginStatus;
  loginError: string | null;
}

export enum UserRole {
  Root,
  Admin,
  Viewer,
}

export enum LoginStatus {
  UNKNOWN,
  LOGGED_OUT,
  LOGGING_IN,
  LOGGED_IN,
  LOGIN_ERROR,
}

export enum Clearance {
  // Anyone can see these. Use it for features that have been recently launched but might go back to unlaunched or under maintenance at some point.
  Public,
  // Features temporarily disabled to public users. Admin and Root can see these.
  UnderMaintenance,
  // Unlaunched features, ready to be previewed before launch. Admin and Root can see these.
  ComingSoon,
  // Features not yet ready for use. Root can see these.
  InDevelopment,
}

const initialState: SessionSlice = {
  userRole: UserRole.Viewer,
  clearances: [Clearance.Public],
  loginStatus: LoginStatus.UNKNOWN,
  loginError: null,
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setLoginError(state: SessionSlice, action: PayloadAction<string>) {
      const loginError = action.payload;
      state.userRole = UserRole.Viewer;
      state.clearances = getClearances(state.userRole);
      state.loginStatus = LoginStatus.LOGIN_ERROR;
      state.loginError = loginError;
    },
    setLoginPending(state: SessionSlice) {
      // Leave role and clearances the same during the server side call.
      state.loginStatus = LoginStatus.LOGGING_IN;
      state.loginError = null;
    },
    setUserRole(state: SessionSlice, action: PayloadAction<UserRole>) {
      const userRole = action.payload;
      state.userRole = userRole;
      state.clearances = getClearances(state.userRole);
      state.loginStatus =
        userRole === UserRole.Viewer ? LoginStatus.LOGGED_OUT : LoginStatus.LOGGED_IN;
      state.loginError = null;
    },
    resetUserRole(state: SessionSlice) {
      state.userRole = UserRole.Viewer;
      state.clearances = getClearances(state.userRole);
      state.loginStatus = LoginStatus.LOGGED_OUT;
      state.loginError = null;
    },
  },
});

export const testLogin = (): AppThunk => async (dispatch, getState) => {
  if (getState().session.loginStatus !== LoginStatus.UNKNOWN) {
    return;
  }
  dispatch(sessionSlice.actions.setLoginPending());
  let userRole: UserRole;
  try {
    userRole = await api.testLogin();
  } catch (e) {
    // Don't log as error or warning, since this happen to all viewers.
    log('debug', `Failed validating existing session: ${e instanceof Error ? e.message : e}`);
    // Unsets pending login.
    userRole = UserRole.Viewer;
  }
  dispatch(sessionSlice.actions.setUserRole(userRole));
};

export const login =
  (username: string, password: string): AppThunk =>
  async (dispatch) => {
    dispatch(sessionSlice.actions.setLoginPending());
    let userRole: UserRole;
    try {
      userRole = await api.login(username, password);
    } catch (e) {
      dispatch(sessionSlice.actions.setLoginError(e instanceof Error ? e.message : '' + e));
      return;
    }
    dispatch(sessionSlice.actions.setUserRole(userRole));
  };

export const logout = (): AppThunk => async (dispatch) => {
  dispatch(sessionSlice.actions.setLoginPending());
  try {
    const userRole = await api.logout();
    if (userRole) dispatch(sessionSlice.actions.resetUserRole());
  } catch (e) {
    // Ignore errors, e.g. not logged in.
    log('warning', `Error logging out: ${e instanceof Error ? e.message : e}`);
  }
};

function getClearances(role: UserRole) {
  const clearances: Clearance[] = [];
  switch (role) {
    case UserRole.Root:
      clearances.push(Clearance.InDevelopment);
    // Fall through.
    case UserRole.Admin:
      clearances.push(Clearance.UnderMaintenance);
      clearances.push(Clearance.ComingSoon);
    // Fall through.
    case UserRole.Viewer:
      clearances.push(Clearance.Public);
      break;
    default:
      throw new Error(`Bad user role: ${role}`);
  }
  return clearances;
}
